import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { compareCalendarDates, toDateInputValue } from '../../utils/dates';

const isActiveUser = (u) => u.isActive ?? true;

const UserOptions = ({ users, activeOnly = false }) =>
    users.filter((u) => !activeOnly || isActiveUser(u)).map((u) => (
        <SelectItem key={u.id} value={String(u.id)}>
            <span className="font-medium">{u.name}</span>
            <span className="ml-2 text-slate-500">{u.email}</span>
        </SelectItem>
    ));

export default function DelegationDialog({ children, delegation, users, onSave }) {

    const isEdit = !!delegation;
    const [open, setOpen] = useState(false);
    const [fromUserId, setFromUserId] = useState('');
    const [toUserId, setToUserId] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setFromUserId(delegation?.fromUser?.userId ? String(delegation.fromUser.userId) : '');
            setToUserId(delegation?.toUser?.userId ? String(delegation.toUser.userId) : '');
            setStartDate(toDateInputValue(delegation?.startDate));
            setEndDate(toDateInputValue(delegation?.endDate));
            setError('');
        }
    }, [open, delegation]);

    const handleSubmit = async () => {
        if (!fromUserId) {
            setError('Select the user delegating their approvals.');
            return;
        }
        if (!toUserId) {
            setError('Select the user receiving the approvals.');
            return;
        }
        if (fromUserId === toUserId) {
            setError('A user cannot delegate to themselves.');
            return;
        }
        const delegate = users.find((u) => u.id === Number(toUserId));
        if (!delegate || !isActiveUser(delegate)) {
            setError('The delegate must be an active user.');
            return;
        }
        if (!startDate || !endDate) {
            setError('Select a start and end date.');
            return;
        }
        if (compareCalendarDates(endDate, startDate) < 0) {
            setError('The end date must be on or after the start date.');
            return;
        }

        const ok = await onSave({
            fromUserId: Number(fromUserId),
            toUserId: Number(toUserId),
            startDate,
            endDate,
        });
        if (ok) {
            setOpen(false);
        } else {
            setError('Something went wrong saving the delegation.');
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <DialogTitle>{isEdit ? 'Edit Delegation' : 'Add Delegation'}</DialogTitle>
                    <DialogDescription>
                        Time-bound reassignment of a user's required approvals to another user. Applies to custom
                        workflows only and is evaluated when a payment is registered.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-4 py-2">
                    <div className="flex flex-col gap-2">
                        <Label>Delegate from</Label>
                        <Select value={fromUserId} onValueChange={setFromUserId}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select user" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <UserOptions users={users.filter((u) => String(u.id) !== toUserId)} />
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label>Delegate to</Label>
                        <Select value={toUserId} onValueChange={setToUserId}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select user" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <UserOptions
                                        users={users.filter((u) => String(u.id) !== fromUserId)}
                                        activeOnly
                                    />
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="delegation-start">Start date</Label>
                            <Input
                                id="delegation-start"
                                type="date"
                                value={startDate}
                                max={endDate || undefined}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="delegation-end">End date</Label>
                            <Input
                                id="delegation-end"
                                type="date"
                                value={endDate}
                                min={startDate || undefined}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>
                    </div>

                    {error && <p className="text-sm text-red-600 mb-0">{error}</p>}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button className="bg-yavrio-green" onClick={handleSubmit}>
                        {isEdit ? 'Save Delegation' : 'Add Delegation'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
